import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';

export interface PayrollPayment {
  id: string;
  employee_id: string;
  payroll_id: string;
  amount_cents: number;
  net_amount_cents: number;
  platform_fee_cents: number;
  stripe_fee_cents: number;
  currency: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled';
  stripe_transfer_id?: string;
  description?: string;
  failure_reason?: string;
  scheduled_at?: string;
  processed_at?: string;
  arrived_at?: string;
  employee?: {
    name: string;
    email: string;
  };
  payroll?: {
    period_start: string;
    period_end: string;
  };
}

export interface PaymentSummary {
  total_payments: number;
  successful_payments: number;
  failed_payments: number;
  pending_payments: number;
  success_rate: number;
  total_amount_cents: number;
  successful_amount_cents: number;
  total_fees_cents: number;
}

export interface PayrollPaymentsHook {
  payments: PayrollPayment[];
  summary: PaymentSummary | null;
  loading: boolean;
  initiating: boolean;
  error: string | null;
  initiateTransfers: (businessId: string, payrollIds: string[], scheduledDate?: string) => Promise<void>;
  retryFailedPayments: (businessId: string, payrollId?: string) => Promise<void>;
  loadPayments: (businessId: string, filters?: PaymentFilters) => Promise<void>;
  refreshPayments: () => Promise<void>;
}

export interface PaymentFilters {
  payroll_id?: string;
  batch_id?: string;
  status?: string;
  employee_id?: string;
  from_date?: string;
  to_date?: string;
}

export const usePayrollPayments = (businessId: string): PayrollPaymentsHook => {
  const [payments, setPayments] = useState<PayrollPayment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<PaymentFilters>({});

  const loadPayments = useCallback(async (businessId: string, filters: PaymentFilters = {}) => {
    if (!businessId) return;
    
    setLoading(true);
    setError(null);
    setCurrentFilters(filters);
    
    try {
      const { data, error } = await supabase.functions.invoke('payroll-disbursement/transfer-status', {
        body: JSON.stringify({
          business_id: businessId,
          ...filters
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to load payments');
      }

      setPayments(data.payments || []);
      setSummary(data.status_summary || null);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load payments';
      setError(errorMessage);
      console.error('Error loading payments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPayments = useCallback(async () => {
    if (businessId) {
      await loadPayments(businessId, currentFilters);
    }
  }, [businessId, currentFilters, loadPayments]);

  const initiateTransfers = useCallback(async (
    businessId: string, 
    payrollIds: string[], 
    scheduledDate?: string
  ) => {
    setInitiating(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('payroll-disbursement/initiate-transfers', {
        body: JSON.stringify({
          business_id: businessId,
          payroll_ids: payrollIds,
          scheduled_date: scheduledDate
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to initiate transfers');
      }

      const { successful_transfers, failed_transfers, total_amount_cents } = data;

      toast.success("Transfers Initiated", { description: `${successful_transfers} payments started successfully. ${failed_transfers > 0 ? `${failed_transfers} failed.` : ''}` });

      // Refresh payments to show updated status
      await refreshPayments();
      
      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to initiate transfers';
      setError(errorMessage);
      toast.error("Transfer Failed", { description: errorMessage });
      throw err;
    } finally {
      setInitiating(false);
    }
  }, [refreshPayments]);

  const retryFailedPayments = useCallback(async (businessId: string, payrollId?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('payroll-disbursement/retry-failed-transfers', {
        body: JSON.stringify({
          business_id: businessId,
          payroll_id: payrollId
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to retry payments');
      }

      const retriedCount = data.retried_payments;
      
      if (retriedCount > 0) {
        toast.success("Payments Retried", { description: `${retriedCount} failed payments have been reset for retry.` });

        // Refresh to show updated status
        await refreshPayments();
      } else {
        toast.info("No Failed Payments", { description: "No eligible failed payments found to retry." });
      }
      
      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to retry payments';
      setError(errorMessage);
      toast.error("Retry Failed", { description: errorMessage });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshPayments]);

  // Load payments on mount and when businessId changes
  useEffect(() => {
    if (businessId) {
      loadPayments(businessId);
    }
  }, [businessId, loadPayments]);

  // Set up real-time subscription for payment status changes
  useEffect(() => {
    if (!businessId) return;

    const subscription = supabase
      .channel(`payroll_payments_${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_payments',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;

          console.log('Payment updated:', payload);

          if (payload.eventType === 'INSERT') {
            const newPayment = payload.new as PayrollPayment;
            setPayments(prev => [newPayment, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            const updatedPayment = payload.new as PayrollPayment;
            setPayments(prev => prev.map(p => p.id === updatedPayment.id ? updatedPayment : p));

            // Show notification for status changes
            if (payload.old.status !== updatedPayment.status) {
              if (updatedPayment.status === 'succeeded') {
                toast.success("Payment Completed", { description: `Payment to ${updatedPayment.employee?.name} was successful.` });
              } else if (updatedPayment.status === 'failed') {
                toast.error("Payment Failed", { description: `Payment to ${updatedPayment.employee?.name} failed: ${updatedPayment.failure_reason}` });
              }
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedPayment = payload.old as PayrollPayment;
            setPayments(prev => prev.filter(p => p.id !== deletedPayment.id));
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [businessId]);

  return {
    payments,
    summary,
    loading,
    initiating,
    error,
    initiateTransfers,
    retryFailedPayments,
    loadPayments,
    refreshPayments,
  };
};